import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { fetchDistributeFeed, setDistributeWorkLiked } from '@/api/distribute'
import type { DistributeFeedItem } from '@/api/distribute'
import { DISTRIBUTE_FEED_EMPTY_PAGE_AUTOFILL_LIMIT } from '@/constants/distribute'
import { getClientUniqueId } from '@/utils/consts'

export const useDistributeFeedStore = defineStore('distributeFeed', () => {
  const items = ref<DistributeFeedItem[]>([])
  const pageNo = ref(0)
  const hasNext = ref(true)
  const loadingInitial = ref(false)
  const loadingMore = ref(false)
  const initialized = ref(false)
  const error = ref<Error | null>(null)

  const loading = computed(() => loadingInitial.value || loadingMore.value)

  function reset() {
    items.value = []
    pageNo.value = 0
    hasNext.value = true
    loadingInitial.value = false
    loadingMore.value = false
    initialized.value = false
    error.value = null
  }

  function findItemById(id: string): DistributeFeedItem | null {
    return items.value.find((item) => item.id === id) ?? null
  }

  function patchItem(id: string, patch: Partial<DistributeFeedItem>) {
    const target = findItemById(id)

    if (!target) {
      return
    }

    Object.assign(target, patch)
  }

  async function fetchNext() {
    if (loading.value) {
      return
    }

    if (!hasNext.value && initialized.value) {
      return
    }

    let nextPageNo = pageNo.value + 1
    const isFirstPage = nextPageNo === 1
    let emptyPageCount = 0

    error.value = null
    loadingInitial.value = isFirstPage
    loadingMore.value = !isFirstPage

    try {
      while (true) {
        const response = await fetchDistributeFeed({
          pageNo: nextPageNo,
          clientUniqueId: getClientUniqueId(),
        })

        const page = response.data

        if (!page) {
          hasNext.value = false
          initialized.value = true
          return
        }

        const existingIds = new Set(items.value.map((item) => item.id))
        const nextItems = page.list.filter((item) => !existingIds.has(item.id))

        if (nextItems.length) {
          items.value = items.value.concat(nextItems)
        }

        // /distribute/feed returns the next page cursor in data.pageNo; the store keeps the
        // last page it actually loaded so pagination requests stay sequential.
        pageNo.value = nextPageNo
        hasNext.value = page.hasNext
        initialized.value = true

        if (nextItems.length || !page.hasNext) {
          return
        }

        emptyPageCount += 1

        if (emptyPageCount >= DISTRIBUTE_FEED_EMPTY_PAGE_AUTOFILL_LIMIT) {
          hasNext.value = false
          return
        }

        nextPageNo += 1
      }
    } catch (nextError) {
      error.value = nextError instanceof Error ? nextError : new Error('发现流加载失败')
    } finally {
      loadingInitial.value = false
      loadingMore.value = false
    }
  }

  async function refresh() {
    reset()
    await fetchNext()
  }

  async function toggleItemLike(id: string) {
    const target = findItemById(id)

    if (!target) {
      throw new Error('未找到对应内容')
    }

    const previousLiked = Boolean(target.liked)
    const previousCount = target.likeNum
    const nextLiked = !previousLiked

    patchItem(id, {
      liked: nextLiked,
      likeNum: Math.max(0, previousCount + (nextLiked ? 1 : -1)),
    })

    try {
      const response = await setDistributeWorkLiked(id, nextLiked)

      if (response.code !== 0) {
        throw new Error(response.msg || '点赞操作失败')
      }
    } catch (nextError) {
      patchItem(id, {
        liked: previousLiked,
        likeNum: previousCount,
      })

      throw nextError instanceof Error ? nextError : new Error('点赞操作失败')
    }
  }

  return {
    items,
    pageNo,
    hasNext,
    loadingInitial,
    loadingMore,
    initialized,
    error,
    loading,
    patchItem,
    reset,
    fetchNext,
    refresh,
    toggleItemLike,
  }
})
